import { AuditLog, AuditMessage, AuditNotification } from '../types';
import { apiFetch } from './apiClient';

function fetchAudit<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiFetch<T>(`/audit${endpoint}`, options);
}

export interface ChannelAuditResponse {
  channel: { id: number; name: string; type: string };
  messages: AuditMessage[];
  total: number;
  auditLogId: number;
}

export interface UserAuditResponse {
  userId: string;
  messages: AuditMessage[];
  total: number;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
}

export interface IntegrityResponse {
  valid: boolean;
  totalChecked: number;
  firstInvalidId?: number;
}

export const auditService = {
  // 채널 메시지 감사 조회
  async getChannelMessages(
    channelId: number,
    reason: string,
    limit = 50,
    offset = 0
  ): Promise<ChannelAuditResponse> {
    return fetchAudit<ChannelAuditResponse>(
      `/channels/${channelId}/messages?reason=${encodeURIComponent(reason)}&limit=${limit}&offset=${offset}`
    );
  },

  // 사용자 메시지 감사 조회
  async getUserMessages(
    userId: string,
    reason: string,
    limit = 50,
    offset = 0,
    channelId?: number
  ): Promise<UserAuditResponse> {
    let url = `/users/${encodeURIComponent(userId)}/messages?reason=${encodeURIComponent(reason)}&limit=${limit}&offset=${offset}`;
    if (channelId) url += `&channelId=${channelId}`;
    return fetchAudit<UserAuditResponse>(url);
  },

  // 감사 로그 조회
  async getLogs(filters?: {
    auditorId?: string;
    actionType?: string;
    targetType?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLogsResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return fetchAudit<AuditLogsResponse>(`/logs?${params.toString()}`);
  },

  // 무결성 검증
  async verifyIntegrity(): Promise<IntegrityResponse> {
    return fetchAudit<IntegrityResponse>('/integrity');
  },

  // 읽지 않은 알림 조회
  async getNotifications(): Promise<AuditNotification[]> {
    return fetchAudit<AuditNotification[]>('/notifications');
  },

  // 알림 읽음 처리
  async markNotificationRead(notificationId: number): Promise<void> {
    await fetchAudit(`/notifications/${notificationId}/read`, { method: 'POST' });
  },

  // 전체 알림 읽음 처리
  async markAllNotificationsRead(): Promise<void> {
    await fetchAudit('/notifications/read-all', { method: 'POST' });
  },
};
